import {
  createDeliveryClient,
  type IContentItem,
  type IDeliveryClient,
} from "@kontent-ai/delivery-sdk"

import { env } from "../../../env"

const COLLECTION_FILTER = "site_expat"

/**
 * Derives cache tags for a Delivery API request so the webhook can
 * invalidate a single item or content type instead of the whole site.
 * Always includes the global tag as a fallback for cases that can't be
 * scoped precisely (see `_all` handling in the webhook route).
 */
function getCacheTagsForUrl(url: string): string[] {
  const tags = [`${env.KONTENT_APP_CODENAME}_all`]

  try {
    const { pathname, searchParams } = new URL(url)
    const segments = pathname.split("/").filter(Boolean)
    const itemsIndex = segments.indexOf("items")

    if (itemsIndex !== -1) {
      const codename = segments[itemsIndex + 1]
      if (codename) {
        tags.push(`${env.KONTENT_APP_CODENAME}_item_${codename}`)
      } else {
        const type = searchParams.get("system.type")
        if (type) {
          tags.push(`${env.KONTENT_APP_CODENAME}_type_${type}`)
        }
      }
    }
  } catch {
    // Malformed URL: keep the global tag only.
  }

  return tags
}

function createNextHttpService(previewMode: boolean) {
  const notImplemented = () => {
    throw new Error("Only GET is supported by the Delivery API")
  }

  return {
    async getAsync<TRawData>(
      call: { url: string },
      options?: { headers?: { header: string; value: string }[] }
    ): Promise<{
      data: TRawData
      status: number
      headers: { header: string; value: string }[]
      rawResponse: Response
      retryStrategy: { options: Record<string, unknown>; retryAttempts: number }
    }> {
      const headers = (options?.headers ?? []).reduce(
        (acc: Record<string, string>, h) => ({ ...acc, [h.header]: h.value }),
        {}
      )
      const res = await fetch(call.url, {
        method: "GET",
        headers,
        ...(previewMode
          ? { cache: "no-store" as const }
          : {
              next: {
                revalidate: 21600, // 6 hours — the webhook is the primary freshness mechanism
                tags: getCacheTagsForUrl(call.url),
              },
            }),
      })

      if (!res.ok) {
        let body = ""
        try {
          body = await res.text()
        } catch {
          // ignore
        }
        console.error(
          `[Kontent HTTP] ${res.status} ${res.statusText} — ${call.url}\n${body}`
        )
        throw new Error(
          `Kontent Delivery API returned ${res.status} ${res.statusText}: ${body}`
        )
      }

      let data: TRawData
      try {
        data = await res.json()
      } catch (e) {
        const raw = "(unable to read body)"
        console.error(
          `[Kontent HTTP] Failed to parse JSON from ${call.url}:`,
          e,
          raw
        )
        throw new Error(
          `Kontent Delivery API returned non-JSON response from ${call.url}`
        )
      }

      return {
        data,
        status: res.status,
        headers: [],
        rawResponse: res,
        retryStrategy: {
          options: {},
          retryAttempts: 0,
        },
      }
    },
    postAsync: notImplemented,
    putAsync: notImplemented,
    patchAsync: notImplemented,
    deleteAsync: notImplemented,
    createCancelToken: () => ({
      cancel: () => {},
      token: undefined,
    }),
  }
}

/**
 * Creates a configured Kontent.ai Delivery SDK client.
 *
 * @param previewMode - When `true`, uses the Preview environment + API key.
 *
 * @returns A ready-to-use `IDeliveryClient`.
 */
export function getDeliveryClient(previewMode: boolean): IDeliveryClient {
  const client = createDeliveryClient({
    environmentId: previewMode
      ? env.KONTENT_PREVIEW_ENV_ID
      : env.KONTENT_ENV_ID,
    previewApiKey: previewMode ? env.KONTENT_PREVIEW_API_KEY : undefined,
    secureApiKey: previewMode
      ? env.KONTENT_PREVIEW_API_KEY
      : env.KONTENT_ENV_API_KEY,
    defaultQueryConfig: {
      usePreviewMode: previewMode,
      useSecuredMode: !previewMode,
    },
    httpService: createNextHttpService(previewMode),
  })

  // Inject collection filter on every .items() call
  const originalItems = client.items.bind(client)
  client.items = function <T extends IContentItem>() {
    return originalItems<T>().equalsFilter(
      "system.collection",
      COLLECTION_FILTER
    )
  } as typeof client.items

  return client
}
